// TaskDetailStatusBadge: Interactive status badge with dropdown for TaskDetailModal
// Allows direct status change from the detail view without opening the edit modal
// CREATED: Part of TaskDetailModal hub refactor
// UPDATED: Removed 'overdue' from selectable options (now automatic via cron)
// Admins can transition FROM overdue back to not_started or completed
// UPDATED: Replaced native disabled with aria-disabled for accessibility (tooltip + keyboard focus preserved)
// UPDATED: Removed client-side blocking validation for completion — managers can complete without required fields (activity log ensures traceability)

import React, { useState, useRef, useEffect } from 'react';
import { Check, Clock, AlertCircle, ChevronDown } from 'lucide-react';

type TaskStatus = 'not_started' | 'completed' | 'overdue';

const STATUS_CONFIG: Record<TaskStatus, {
  label: string;
  icon: typeof Check;
  bgColor: string;
  textColor: string;
}> = {
  completed: {
    label: 'Terminée',
    icon: Check,
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
  },
  overdue: {
    label: 'En retard',
    icon: AlertCircle,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
  },
  not_started: {
    label: 'Non démarrée',
    icon: Clock,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
  },
};

const SELECTABLE_STATUSES: TaskStatus[] = ['not_started', 'completed'];

interface TaskDetailStatusBadgeProps {
  currentStatus: TaskStatus;
  onStatusChange: (newStatus: TaskStatus) => Promise<void>;
  disabled?: boolean;
  isAdmin?: boolean;
}

export function TaskDetailStatusBadge({
  currentStatus,
  onStatusChange,
  disabled,
  isAdmin = false,
}: TaskDetailStatusBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const availableStatuses = SELECTABLE_STATUSES;

  const isInteractive = isAdmin || currentStatus !== 'overdue';

  const handleSelect = async (status: TaskStatus) => {
    if (status === currentStatus) {
      setIsOpen(false);
      return;
    }
    setIsOpen(false);
    setIsSaving(true);
    try {
      await onStatusChange(status);
    } finally {
      setIsSaving(false);
    }
  };

  const config = STATUS_CONFIG[currentStatus];
  const Icon = config.icon;
  const isDisabled = disabled || isSaving || !isInteractive;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      className="relative"
      ref={dropdownRef}
      title={isInteractive ? 'Cliquer pour modifier le statut' : 'Statut défini automatiquement'}
    >
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`inline-flex items-center gap-1 px-2.5 py-1 ${config.bgColor} ${config.textColor} text-xs font-medium rounded transition-opacity select-none ${
          isDisabled
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer hover:opacity-80'
        }`}
      >
        {isSaving ? (
          <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <Icon size={12} />
        )}
        {config.label}
        {isInteractive && (
          <ChevronDown
            size={11}
            className={`ml-0.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50 min-w-[190px]">
          {availableStatuses.map((status) => {
            const sc = STATUS_CONFIG[status];
            const StatusIcon = sc.icon;
            const isSelected = currentStatus === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => handleSelect(status)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
              >
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 ${sc.bgColor} ${sc.textColor} text-xs font-medium rounded whitespace-nowrap`}>
                  <StatusIcon size={12} />
                  {sc.label}
                </span>
                {isSelected && (
                  <Check size={13} className="text-primary-600 flex-shrink-0 ml-2" />
                )}
              </button>
            );
          })}
        </div>
      )}

    </div>
  );
}
